// ─── Is the US market open right now? ────────────────────────────────────────
//
// One function for the whole app (2026-09-26). On a Saturday the app said
// "היום", "LIVE" and "מצב השוק כרגע" about numbers from Friday's close.
//
// By New York wall-clock time, with NYSE holidays from Finnhub's market-holiday
// calendar (the source the economic calendar already uses · a live source with
// a date, not a hand-written list):
//   open    · a trading day, 09:30 until the close (16:00, or the early close
//             Finnhub states for a half day)
//   pre     · a trading day, 04:00–09:30
//   post    · a trading day, after the close until 20:00
//   closed  · weekend, holiday, or the night
// `lastSession` is the date of the most recent regular session that has
// OPENED: today once 09:30 has passed on a trading day, otherwise the trading
// day before.

import { useEffect, useState } from "react";
import { callFinnhub } from "./finnhubClient";

const TZ = "America/New_York";

function nyParts(now) {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23", weekday: "short",
  }).formatToParts(now).map(x => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, minutes: Number(p.hour) * 60 + Number(p.minute), weekday: p.weekday };
}

const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const weekdayOf = iso => new Date(`${iso}T12:00:00Z`).getUTCDay();   // 0 Sun … 6 Sat

// holidays: [{ atDate: "YYYY-MM-DD", tradingHour: "" | "09:30-13:00" }]
function dayInfo(iso, holidays) {
  const wd = weekdayOf(iso);
  if (wd === 0 || wd === 6) return { trading: false, reason: "weekend" };
  const h = holidays.find(x => x.atDate === iso);
  if (!h) return { trading: true, close: 16 * 60 };
  const m = /(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/.exec(h.tradingHour || "");
  if (!m) return { trading: false, reason: "holiday", name: h.eventName };
  return { trading: true, close: Number(m[3]) * 60 + Number(m[4]), early: true };
}

export function getMarketStatus(now = new Date(), holidays = []) {
  const { date, minutes } = nyParts(now);
  const today = dayInfo(date, holidays);
  let state = "closed";
  if (today.trading) {
    if (minutes >= 9 * 60 + 30 && minutes < today.close) state = "open";
    else if (minutes >= 4 * 60 && minutes < 9 * 60 + 30) state = "pre";
    else if (minutes >= today.close && minutes < 20 * 60) state = "post";
  }
  // The latest session that has opened.
  let last = date;
  if (!(today.trading && minutes >= 9 * 60 + 30)) {
    last = addDays(date, -1);
    for (let i = 0; i < 10 && !dayInfo(last, holidays).trading; i++) last = addDays(last, -1);
  }
  return { state, open: state === "open", date, lastSession: last, reason: today.reason || null, holidayName: today.name || null, nextOpen: nextOpenAfter(now, date, minutes, holidays) };
}

// ─── When the market opens next (sprint 19, stage 2) ────────────────────────
// The New York wall-clock moment `minutes` after midnight on `iso`, as a Date.
// New York's offset is read from Intl, not assumed: start from UTC−4, look at
// what New York says, and correct by the difference (twice covers the day the
// clock changes).
export function nyWallTime(iso, minutes) {
  let t = Date.parse(`${iso}T00:00:00Z`) + minutes * 60000 + 4 * 3600000;
  for (let i = 0; i < 2; i++) {
    const p = nyParts(new Date(t));
    const days = (Date.parse(`${p.date}T00:00:00Z`) - Date.parse(`${iso}T00:00:00Z`)) / 86400000;
    t += (minutes - (days * 1440 + p.minutes)) * 60000;
  }
  return new Date(t);
}

// The next 09:30 New York on a trading day, after now · ISO string, or null
// when none falls in the next ten days.
function nextOpenAfter(now, date, minutes, holidays) {
  const OPEN = 9 * 60 + 30;
  if (dayInfo(date, holidays).trading && minutes < OPEN) return nyWallTime(date, OPEN).toISOString();
  let d = date;
  for (let i = 0; i < 10; i++) {
    d = addDays(d, 1);
    if (dayInfo(d, holidays).trading) return nyWallTime(d, OPEN).toISOString();
  }
  return null;
}

// "16:30" · a moment on the Israel clock and on the New York clock.
const clock = (d, timeZone) => new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone });
export const israelClock = d => clock(d, "Asia/Jerusalem");
export const nyClock = d => clock(d, "America/New_York");

// "45 דק׳" · "3 שע׳ 20 דק׳" · whole minutes, rounded up (a minute and a half
// to the open is "2 דק׳", never "1").
export function untilText(ms) {
  const m = Math.max(1, Math.ceil(ms / 60000));
  const h = Math.floor(m / 60), r = m % 60;
  if (!h) return `${m} דק׳`;
  return r ? `${h} שע׳ ${r} דק׳` : `${h} שע׳`;
}

// The badge above the home screen's brief card. The state is New York's
// (above); the times are given on both clocks. Tel Aviv's own session is not
// stated: it has no live source in the app (utils/marketHours.js).
// → { state, text, detail } · `detail` is the next open, when there is one.
export function marketBadge(status, now = new Date()) {
  const open = status.nextOpen ? new Date(status.nextOpen) : null;
  const at = open ? `${israelClock(open)} שעון ישראל · ${nyClock(open)} ניו יורק` : null;
  if (status.state === "pre") {
    return { state: "pre", text: open ? `טרום מסחר · נפתח בעוד ${untilText(open - now)}` : "טרום מסחר", detail: at };
  }
  if (status.state === "open") return { state: "open", text: "המסחר פתוח", detail: null };
  if (status.state === "post") return { state: "post", text: "אחרי הסגירה", detail: null };
  if (!open) return { state: "closed", text: "השוק סגור", detail: null };
  const day = WEEKDAY_HE[new Date(`${nyParts(open).date}T12:00:00Z`).getUTCDay()];
  return { state: "closed", text: `השוק סגור · נפתח ${day} ${israelClock(open)}`, detail: `שעון ישראל · ${nyClock(open)} ניו יורק`, holiday: status.reason === "holiday" };
}

// How many New York sessions have opened after `iso` (a YYYY-MM-DD date of
// an end-of-day figure), up to the last session · 0 for a figure from the
// last session, 1 for the one before it. null for no date. Holidays from
// Finnhub, as the rest of this file (20/10/2026: oil from FRED said
// "נכון ל-14/10" on the 20th and nothing marked it as old).
export function sessionsBehind(iso, status, hol = holidays) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso) || !status?.lastSession) return null;
  const from = String(iso).slice(0, 10);
  if (from >= status.lastSession) return 0;
  let n = 0;
  for (let d = addDays(from, 1), i = 0; d <= status.lastSession && i < 400; d = addDays(d, 1), i++) {
    if (dayInfo(d, hol).trading) n += 1;
  }
  return n;
}

// An end-of-day figure more than one session behind is old, and says so.
export const STALE_SESSIONS = 1;
export function staleDate(iso, status, hol = holidays) {
  const n = sessionsBehind(iso, status, hol);
  return n != null && n > STALE_SESSIONS ? n : null;
}

// "ביום שני" · the weekday of the last session.
export function sessionWeekday(status) {
  return WEEKDAY_HE[weekdayOf(status.lastSession)];
}

const WEEKDAY_HE = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

// "שישי 25/09"
const sessionDay = iso => `${WEEKDAY_HE[weekdayOf(iso)]} ${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
export const lastSessionDay = status => sessionDay(status.lastSession);

// "יום המסחר האחרון (שישי 25/09)"
export function lastSessionLabel(status) {
  return `יום המסחר האחרון (${sessionDay(status.lastSession)})`;
}

// ─── One closing label for every index (bug round, 17/10/2026) ──────────────
// After the close each index carries the moment ITS provider stamped its
// final value: the S&P and the Dow 16:42 New York time, the Nasdaq Composite
// 17:15 (measured on Yahoo, 16/10). In Israel that is 23:42 and 00:15, so the
// same session read as two different times, one of them on another day.
// While the market is not open, a quote from the last session is labelled by
// the session · "סגירה · שישי 16/10" · and its exact time stays in the
// detail view. A quote from an older session keeps its own date: the label
// is only true of the session it names.
export function closeLabel(status) {
  return `סגירה · ${sessionDay(status.lastSession)}`;
}

// Whether a quote stamped at `ts` is from the last session (New York date).
export function fromLastSession(ts, status) {
  const t = new Date(ts);
  if (!ts || !Number.isFinite(t.getTime())) return false;
  return nyParts(t).date === status.lastSession;
}

// The label a closed market puts on an index quote, or null when the market
// is open or the quote is not from the last session.
export function sessionCloseMark(ts, status) {
  return !status.open && fromLastSession(ts, status) ? closeLabel(status) : null;
}

// "היום" while the market is open; the last session otherwise.
export function dayLabel(status) {
  return status.open ? "היום" : lastSessionLabel(status);
}

// ── Holidays, once per session; refresh the state every minute ─────────────
let holidays = [];
let loaded = null;
function loadHolidays() {
  if (!loaded) {
    loaded = callFinnhub("holidays")
      .then(d => { holidays = Array.isArray(d?.holidays?.data) ? d.holidays.data : []; })
      .catch(() => {});
  }
  return loaded;
}

export function useMarketStatus() {
  const [status, setStatus] = useState(() => getMarketStatus(new Date(), holidays));
  useEffect(() => {
    let alive = true;
    const tick = () => { if (alive) setStatus(getMarketStatus(new Date(), holidays)); };
    loadHolidays().then(tick);
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return status;
}
